import {
  APP_CONFIG,
  authTktState,
  currentUserState,
  isMobile,
  ROUTES,
  selectedSellerNumberState,
  useBreakpointIsLessThan
} from '@sd/shared';
import { LocationDescriptorObject } from 'history';
import { forwardRef, useMemo } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import styled, { useTheme } from 'styled-components';
import { Icon } from '@sd/components';

type Props = {
  isScreenCompact?: boolean;
};

type MenuLink = {
  target?: string;
  title: string;
} & ({ internalUrl: LocationDescriptorObject } | { externalUrl: string });

// #main-site-nav is used for analytics only, do not use for any other purpose
const analyticsId = 'main-site-nav';

export const SellerMenu = ({ isScreenCompact }: Props) => {
  let isCompact = useBreakpointIsLessThan('lg');
  if (isScreenCompact) isCompact = isScreenCompact;
  const theme = useTheme();
  const sellerNumber = useRecoilValue(selectedSellerNumberState);
  const authTkt = useRecoilValue(authTktState)!;
  const currentUser = useRecoilValue(currentUserState);
  const betaUserFeatures = currentUser?.betaUserFeatures;
  const auctionRunToggleOn = betaUserFeatures?.includes('bw_auction_runs');
  const auctionSalesToggleOn = betaUserFeatures?.includes('bw_auction_sales_react');
  const showExpressDirectLink = betaUserFeatures?.includes('Redline_expressdirect_subnav') ?? false;
  const menuItems = useMemo(() => {
    if (!currentUser) return [];
    const prodLikeEnvironment = ['uat', 'prod'].includes(APP_CONFIG.build.environment);
    const items: Array<MenuLink | false> = [
      currentUser.employee && {
        title: 'Account Select',
        ...{ internalUrl: ROUTES.admin.accountSelect.locationFor({}) }
      },
      { externalUrl: ROUTES.external.legacySellerDashboard.sellingCenter.externalUrlFor(), title: 'Selling Center' },
      {
        title: 'View Auction Runs',
        ...(auctionRunToggleOn
          ? { internalUrl: ROUTES.auctionRuns.auctionRuns.locationFor() }
          : { externalUrl: ROUTES.external.inventory.auctionRuns.externalUrlFor() })
      },
      {
        externalUrl: ROUTES.external.inventory.search.externalUrlFor(),
        title: 'Inventory Manager Classic'
      },
      {
        title: 'Auction Sales',
        ...(auctionSalesToggleOn
          ? { internalUrl: ROUTES.sales.base.locationFor() }
          : { externalUrl: ROUTES.external.legacySellerDashboard.sales.externalUrlFor() })
      },
      { externalUrl: ROUTES.external.legacySellerDashboard.registration.externalUrlFor(), title: 'Registration' },
      !isCompact && { externalUrl: ROUTES.external.legacySellerDashboard.import.externalUrlFor(), title: 'Import' },
      {
        title: 'Account Statistics',
        internalUrl: ROUTES.statistics.accountStatistics.locationFor()
      },
      { externalUrl: ROUTES.external.legacySellerDashboard.inventory.externalUrlFor({}), title: 'Inventory' },
      {
        title: 'Listing Manager',
        ...(prodLikeEnvironment
          ? { externalUrl: ROUTES.external.legacySellerDashboard.listingManager.externalUrlFor({}) }
          : { internalUrl: ROUTES.listingManager.listingManager.locationFor({}) })
      },
      { internalUrl: ROUTES.addVehicle.base.locationFor(), title: 'Add Vehicle' },
      showExpressDirectLink && {
        externalUrl: ROUTES.external.legacySellerDashboard.expressDirect.externalUrlFor(),
        title: `Express Direct${String.fromCharCode(8482)}`
      },
      {
        externalUrl: ROUTES.external.webViews.addressBook.externalUrlFor({
          token: authTkt,
          username: currentUser.username
        }),
        title: 'Address Book',
        target: '_blank'
      },
      {
        externalUrl: ROUTES.external.webViews.requestConcierge.externalUrlFor({
          manheimAccountNumber: sellerNumber ? String(sellerNumber) : undefined,
          username: currentUser.username
        }),
        title: 'Request Concierge',
        target: '_blank'
      },
      // Only for dev
      APP_CONFIG.build.environment === 'dev' && {
        internalUrl: ROUTES.appShell.logout.locationFor(),
        title: '⚙ Logout'
      }
    ];
    return items.filter((i): i is MenuLink => !!i);
  }, [currentUser, authTkt, sellerNumber, isCompact]);

  const mobileBack = () => {
    //question: if on first page, show disabled or not at all...  or is there a way to dismiss the panel?
    if (!isMobile() || window.history.length <= 1) return null;
    return (
      <Button variant="link" as="a" className="mb-2" size="sm" onClick={() => window.history.back()}>
        <Icon variant="chevron-left-light" className="mr-1" />
        Previous Page
      </Button>
    );
  };

  if (!currentUser) return null;

  if (isCompact) {
    return (
      <MobileHeaderContainer>
        <Dropdown id={analyticsId} className="mb-2">
          <Dropdown.Toggle as={MenuButton} />
          <Dropdown.Menu>
            {menuItems.map(item =>
              'internalUrl' in item ? (
                <Dropdown.Item
                  key={item.title}
                  as={InternalLink}
                  to={item.internalUrl}
                  style={{ color: theme.colors.secondary }}
                  className="ml-0"
                >
                  {item.title}
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  key={item.title}
                  as="a"
                  href={item.externalUrl}
                  target={item.target}
                  style={{ color: theme.colors.secondary }}
                  className="ml-0"
                >
                  {item.title}
                </Dropdown.Item>
              )
            )}
          </Dropdown.Menu>
        </Dropdown>
        {mobileBack()}
      </MobileHeaderContainer>
    );
  }

  return (
    <div id={analyticsId} style={{ overflowX: 'hidden' }}>
      <MenuContainer className="mb-4">
        {menuItems.map(item =>
          'internalUrl' in item ? (
            <InternalLink key={item.title} to={item.internalUrl}>
              {item.title}
            </InternalLink>
          ) : (
            <a key={item.title} href={item.externalUrl} target={item.target}>
              {item.title}
            </a>
          )
        )}
      </MenuContainer>
    </div>
  );
};

const InternalLink = styled(Link)``;
const MenuContainer = styled.div`
  margin-left: -1.6em;

  ${InternalLink}, a {
    display: inline-block;
    line-height: 2rem;

    &:before {
      color: ${({ theme }) => theme.colors.secondary};
      width: 1.6em;
      display: inline-block;
      text-align: center;
      content: '|';
    }
  }
`;

const MobileHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;

  .dropdown-menu {
    z-index: 1039;
  }
`;

const MenuButton = forwardRef<HTMLButtonElement, { onClick: () => void }>(({ onClick }, ref) => {
  return (
    <Button variant="outline-secondary" ref={ref} onClick={onClick}>
      Menu
    </Button>
  );
});
